import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fetchUserByEmail, logout } from "../../../../store/actions/userActions";
import AuthFrame from "../../../auth/v2/AuthFrame";
import { TitleBlock, Actions, PrimaryBtn } from "../../../auth/v2/pieces";
import { Loading } from "../../../ForBrands/ui/BrandUI";
import { legacySportKey, loadStateAssociations } from "../../../ForBusiness/Cacheshare/taxonomy";
import { getCoachOnboarding, saveCoachProgress, completeOnboarding, requestTeam, bindSession, sessionChanged, SESSION_CHANGED } from "./coachOnboardingServices";
import {
  AWAITING,
  BACK,
  EMPTY_FILTERS,
  TOPBAR,
  cascadeLabels,
  changeFilters,
  filtersFromDraft,
  openingStep,
  reachableStep,
  teamBinding,
  teamName,
  toDraft,
} from "./coachOnboardingModel";
import { COACH_VERIFICATION_PATH } from "../coachPaths";
import { useWizardSession } from "../../../auth/v2/useWizardSession";
import SportStep from "./steps/SportStep";
import TierRegionStep from "./steps/TierRegionStep";
import LeagueStep from "./steps/LeagueStep";
import TeamStep from "./steps/TeamStep";
import TeamRequestStep from "./steps/TeamRequestStep";

// Coach onboarding V2 (Figma "Coach Onboarding" 05 to 08a). The account
// exists from the sign-up name step (Step 1 of 4); here the coach picks
// sport and gender, tier and region, league and team, and submits for admin
// verification, then waits on /coach/verification (09). Every step saves its
// place and the picks on the server, so the coach resumes where they left
// off. A submitted coach can reopen the wizard from 09 to change the team.

const CoachOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user?.user || {});
  const { alive, end, stop, sessionLost } = useWizardSession({ bindSession, sessionChanged });
  const [state, setState] = useState(null);
  const [step, setStep] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [team, setTeam] = useState(null);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState("");

  const failed = (e) => {
    if (e?.code === SESSION_CHANGED) stop();
  };

  // Saves go out one after another, so the server keeps the last move even
  // when the coach clicks faster than a save answers. Never rejects.
  const saves = useRef(Promise.resolve());
  const save = (next, draft) => {
    saves.current = saves.current.then(() => saveCoachProgress(next, draft)).catch(failed);
    return saves.current;
  };

  // The signed-in user must say "onboarded" (and the verification status)
  // before a route guard sees it, or it sends the coach straight back here.
  const syncUser = (status) => {
    dispatch({
      type: "FETCH_USER_BY_EMAIL",
      payload: {
        ...user,
        onboardingCompleted: true,
        options: { ...(user.options || {}), onboardingCompleted: true },
        coachVerification: { ...(user.coachVerification || {}), status },
      },
    });
    const email = localStorage.getItem("myEmail");
    if (email) dispatch(fetchUserByEmail(email));
  };

  useEffect(() => {
    (async () => {
      try {
        const s = await getCoachOnboarding();
        if (!alive()) return;
        const opening = openingStep(s);
        if (opening.redirect) {
          syncUser(s.verification.status);
          navigate(opening.redirect, { replace: true });
          return;
        }
        const draft = s.progress?.draft || {};
        // State associations come from Cerebro's catalogue: load them
        // before a saved state pick is read back.
        if (draft.body?.value === "state_hs") await loadStateAssociations(draft.gender);
        if (!alive()) return;
        const restored = filtersFromDraft(draft);
        setFilters(restored);
        setTeam(draft.team || null);
        setRequest(draft.request || null);
        setState(s);
        setStep(reachableStep(opening.step, restored));
      } catch (e) {
        if (e.code === SESSION_CHANGED) stop();
        else if (alive()) setError(e.message || "We could not load your setup.");
      }
    })();
    // Runs once: where the wizard opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The only way between steps; each move saves the place and the picks.
  const go = (next, over = {}) => {
    if (!alive()) return;
    const now = { filters, team, request, ...over };
    setFilters(now.filters);
    setTeam(now.team);
    setRequest(now.request);
    setStep(next);
    window.scrollTo({ top: 0 });
    save(next, toDraft(now.filters, now.team, now.request));
  };

  const updateFilters = (next) => {
    const changed = changeFilters(filters, next);
    setFilters(changed.filters);
    if (changed.scopeChanged) setTeam(null);
  };

  const skipLeague = () => {
    const changed = changeFilters(filters, { body: null });
    go("team", { filters: changed.filters, ...(changed.scopeChanged ? { team: null } : {}) });
  };

  // Save and exit: a submitted coach goes back to the verification screen;
  // before that every other page sends the coach here, so it logs out.
  const leave = async () => {
    const alreadySubmitted = !!state.onboardingCompleted;
    if (!alreadySubmitted) end();
    await save(step, toDraft(filters, team, request));
    // Another tab signed in or out meanwhile: never log that session out.
    if (sessionChanged()) {
      stop();
      return;
    }
    if (alreadySubmitted) {
      navigate(COACH_VERIFICATION_PATH);
      return;
    }
    toast.success("Progress saved. Log in any time to finish setting up.");
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const afterSubmit = async (draft) => {
    if (!alive()) return;
    await save(AWAITING, draft);
    if (!alive()) return;
    syncUser("pending");
    navigate(COACH_VERIFICATION_PATH, { replace: true });
  };
  // The combined sport key the onboarding endpoints take (mbb, wbb, fb).
  const sportKey = () => legacySportKey(filters.sport?.value, filters.gender?.value);

  const submitTeam = async () => {
    const { league, leagueKey, teamSource } = teamBinding(team);
    await completeOnboarding({
      sport: sportKey(),
      league,
      leagueKey,
      teamId: String(team.id),
      teamSource,
      currentTeam: teamName(team),
      playerId: "",
      cascade: cascadeLabels(filters, team),
    });
    await afterSubmit(toDraft(filters, team, null));
  };

  // The team request names the level in the server's words (College, Pro,
  // HS / Grassroots) beside the tier key, so the request and its admin
  // email carry the real level.
  const submitRequest = async (req, confirmed) => {
    await requestTeam({
      teamName: req.teamName.trim(),
      city: req.city.trim(),
      state: req.state.trim(),
      sport: sportKey(),
      gender: filters.gender?.value || "",
      tier: filters.tier?.value || "",
      level: filters.tier?.name || "",
      cascade: cascadeLabels(filters, null),
      ...(confirmed ? { confirmedNoMatch: true } : {}),
    });
    await afterSubmit(toDraft(filters, null, req));
  };

  if (sessionLost) {
    return (
      <AuthFrame title="Onboarding">
        <TitleBlock eyebrow="Session changed" title="This page is out of date" sub="You signed out or signed in to a different account in another tab. Nothing more was saved from this page." />
        <Actions>
          <PrimaryBtn onClick={() => window.location.reload()}>Reload</PrimaryBtn>
        </Actions>
      </AuthFrame>
    );
  }
  if (error) {
    return (
      <AuthFrame title="Onboarding">
        <TitleBlock eyebrow="Setup" title="We could not load your setup" sub={error} />
        <Actions>
          <PrimaryBtn onClick={() => window.location.reload()}>Try again</PrimaryBtn>
        </Actions>
      </AuthFrame>
    );
  }
  if (!state || !step) {
    return (
      <AuthFrame title="Setting up">
        <Loading label="Loading your setup..." />
      </AuthFrame>
    );
  }

  const body = () => {
    switch (step) {
      case "sport":
        return <SportStep firstName={state.firstName} filters={filters} onChange={updateFilters} onContinue={() => go("tier")} />;
      case "tier":
        return <TierRegionStep filters={filters} onChange={updateFilters} onContinue={() => go("league")} />;
      case "league":
        return <LeagueStep filters={filters} onChange={updateFilters} onContinue={() => go("team")} />;
      case "team":
        return (
          <TeamStep
            filters={filters}
            team={team}
            onSearch={(teamSearch) => setFilters((f) => ({ ...f, teamSearch }))}
            onPick={setTeam}
            onSubmit={submitTeam}
            onCantFind={() => go("teamRequest", { request: { city: "", state: "", ...request, teamName: filters.teamSearch.trim() || request?.teamName || "" } })}
          />
        );
      case "teamRequest":
        return (
          <TeamRequestStep
            filters={filters}
            request={request}
            onRequestChange={setRequest}
            onSubmit={submitRequest}
            onPickMatch={(picked) => go("team", { team: picked })}
            onBackToSearch={() => go("team")}
          />
        );
      default:
        return null;
    }
  };

  const bar = TOPBAR[step];
  return (
    <AuthFrame
      progress={bar.progress}
      onBack={BACK[step] ? () => go(BACK[step]) : undefined}
      action={bar.skip ? { label: "Skip", onClick: skipLeague } : { label: "Save and exit", onClick: leave }}
    >
      {body()}
    </AuthFrame>
  );
};

export default CoachOnboarding;
